"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useInView } from "react-intersection-observer";
import WebStory from "./WebStory";
import styles from "./webstorieslist.module.scss";
import Link from "next/link";
import Loader from "../atoms/Loader/Loader";

const WebStoriesList = ({ initialStories = [] }) => {
  const [stories, setStories] = useState(initialStories);
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchingRef = useRef(false);
  const { ref, inView } = useInView({ threshold: 0.1 });

  const fetchStories = useCallback(async (pageToFetch = page) => {
    if (fetchingRef.current || !hasMore) return;
    if(stories.length < 12) return;
    fetchingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_ADMIN_BACKEND_BASEURL}/api/webstories?page=${pageToFetch}&limit=12`
      );
      if (!response.ok) throw new Error("Failed to fetch stories");

      const res = await response.json();
      const { list, pagination } = res.data;
      setError(null)
      setStories(prev => [...prev, ...list]);
      setHasMore(list.length > 0 && stories.length + list.length < pagination.totalRecord);
      if (list.length === 12) setPage(prev => prev + 1);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [page, hasMore, stories.length]);

  useEffect(() => {
    if (inView && hasMore && !loading && !fetchingRef.current && !error) {
      fetchStories();
    }
  }, [inView, hasMore, loading, error]);

  const handleRetry = useCallback(() => {
    setPage(2);
    setHasMore(true);
    fetchStories(2);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumb}>
        <Link prefetch={false} href={"/"} className={styles.fadeLink}>Home </Link> <span className={styles.fadeLink}>/</span>  <span>Web Stories</span>
      </div>  
      <h1 className={styles.heading}>Explore visual stories</h1>  
      
      <div className={styles.storiesList}>
        {stories.map((item) => (
          <WebStory key={item?.storyId} item={item} />
        ))}
      </div>
      
      {loading && stories.length === 0 && (
        <div>
            <Loader/>
            <p className={styles.loading}>Loading stories...</p>
        </div>
      )}
      
      {loading && stories.length > 0 && (
        <div>
            <Loader/>
            <p className={styles.loadingMore}>Loading more stories...</p>
        </div>
      )}

      {error && (
        <div className={styles.errorContainer}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.retryButton}
            onClick={handleRetry}
            disabled={loading}
          >
            {loading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      )}

      {!loading && stories.length === 0 && !error && (
        <p className={styles.noStories}>No stories available.</p>
      )}
      {hasMore && !error && <div ref={ref} className={styles.loader}></div>}
      </div>
  );
};

export default WebStoriesList;
